import { useMemo, useState } from "react";

import ComboBoxMultiple from "components/ComboBoxMultiple";
import ComboBoxMultipleDefaultItem from "components/ComboBoxMultiple/ComboBoxMultipleDefaultItem";
import ComboBoxMultipleInput from "components/ComboBoxMultiple/Input";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import ListBoxSection from "ds/components/ListBox/Group";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";

import {
  filterRegionSections,
  groupRegionsIntoSections,
  RegionOption,
  RegionSection,
} from "./helpers";

type RegionsFieldProps = {
  value: string[];
  onChange: (value: string[]) => void;
  options: RegionOption[];
  isLoading?: boolean;
  error?: string;
  helperText?: string;
  noMargin?: boolean;
};

const RegionsField = ({
  value,
  onChange,
  options,
  isLoading,
  error,
  helperText,
  noMargin,
}: RegionsFieldProps) => {
  const [query, setQuery] = useState("");

  const sections = useMemo(() => groupRegionsIntoSections(options), [options]);
  const flatItems = useMemo(() => sections.flatMap((section) => section.items), [sections]);
  const visibleSections = useMemo(() => filterRegionSections(sections, query), [sections, query]);

  return (
    <Box direction="column" gap="medium" margin={noMargin ? undefined : "x-large 0 0"}>
      <ComboBoxMultiple<RegionSection, string, { name: string }>
        key={options.length === 0 ? "loading" : "ready"}
        label="Regions"
        placeholder="Type to search or select from the list"
        tooltipInfo={
          <>
            <TooltipModalTitle>Regions</TooltipModalTitle>
            <TooltipModalBody align="start">
              The AWS regions to scan for unmanaged resources. Only resources located in the
              selected regions are discovered — scanning many regions at once can hit your
              provider&apos;s API rate limits.
            </TooltipModalBody>
          </>
        }
        items={visibleSections}
        values={value}
        error={error}
        helperText={helperText}
        isLoading={isLoading}
        isDisabled={isLoading}
        inputValue={query}
        onInputChange={setQuery}
        renderInput={(inputProps) => <ComboBoxMultipleInput {...inputProps} items={flatItems} />}
        onChange={(region) => {
          if (!region) {
            return;
          }

          onChange(
            value.includes(region) ? value.filter((item) => item !== region) : [...value, region]
          );
        }}
        onBulkValuesChange={onChange}
      >
        {(section) => (
          <ListBoxSection id={section.name} label={section.label} items={section.items}>
            {(item) => (
              <ComboBoxMultipleDefaultItem
                checkboxVisible
                id={item.value}
                label={item.label}
                isSelected={value.includes(item.value)}
              />
            )}
          </ListBoxSection>
        )}
      </ComboBoxMultiple>

      {value.length > 3 ? (
        <Banner variant="warning">
          You&apos;ve selected {value.length} regions — scanning that many at once may exceed your
          cloud provider&apos;s API rate limits, leading to incomplete results.
        </Banner>
      ) : (
        <Banner variant="info">
          Too many regions in a single scan may exceed your cloud provider&apos;s API rate limits,
          leading to incomplete results.
        </Banner>
      )}
    </Box>
  );
};

export default RegionsField;
